"use client"

import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { ErrorMessage } from "@/app/error"
import { LeaderboardPageType } from "@/app/navigation/leaderboard-types"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { Trans } from "@lingui/react/macro"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { usePathname } from "next/navigation"
import { Suspense, useEffect } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useCollectionLeaderboardReatom } from "../hooks"
import {
  CollectionLeaderboardFiltersMoleculeReatom,
  CollectionLeaderboardMoleculeReatom,
} from "../store.reatom"
import { getUrlSearchParams } from "../utils"
import { LeaderboardTable } from "./table"
import {
  LeaderboardTableLayout,
  LeaderboardTableSkeleton,
} from "./table/template"
import { useLeaderboardChain } from "./use-leaderboard-chain"

/**
 * Solana has no floor history to chart: its floors mostly come from other
 * marketplaces, which publish none.
 */
function useHideFloorChart() {
  return useLeaderboardChain() === Blockchain.SOLANA
}

export const CollectionLeaderboardView = reatomComponent(() => {
  const hideFloorChart = useHideFloorChart()

  return (
    <ErrorBoundary fallback={<ErrorFallback hideFloorChart={hideFloorChart} />}>
      <Suspense
        fallback={
          <CollectionLeaderboardSkeleton hideFloorChart={hideFloorChart} />
        }
      >
        <CollectionLeaderboardContent />
      </Suspense>
    </ErrorBoundary>
  )
}, "CollectionLeaderboardView")

export const CollectionLeaderboardContent = reatomComponent(() => {
  const { queryInput } = useMolecule(CollectionLeaderboardMoleculeReatom)
  const { statisticPeriod, sortBy, sortDirection } = useMolecule(
    CollectionLeaderboardFiltersMoleculeReatom,
  )
  const statisticPeriodValue = queryInput().statisticPeriod
  const { data: leaderboardItems } = useCollectionLeaderboardReatom()

  const pathname = usePathname()
  const isHolding = pathname.includes(LeaderboardPageType.HOLDING)
  const { selectedBlockchain } = useMolecule(BlockchainFiltersMoleculeReatom)
  const currentBlockchain = selectedBlockchain()
  const showBlockchain = currentBlockchain === "ALL"
  const hideFloorChart = useHideFloorChart()
  const leaderboardChain = useLeaderboardChain()

  // Sync URL with filter state - moved from Reatom effect to React useEffect
  // to avoid "Cannot update component while rendering" error
  const statisticPeriodVal = statisticPeriod()
  const sortByVal = sortBy()
  const sortDirectionVal = sortDirection()

  useEffect(() => {
    if (typeof window === "undefined") return
    // The chain filter follows the URL a render late after a chain switch or
    // Back; until it does, the period is resolved for the wrong chain.
    if (currentBlockchain !== leaderboardChain) return

    const url = new URL(window.location.href)
    const search = getUrlSearchParams(
      url,
      { statisticPeriod: statisticPeriodVal },
      { field: sortByVal, direction: sortDirectionVal },
      leaderboardChain,
    )

    url.search = search.toString()
    window.history.replaceState(null, "", url)
  }, [
    statisticPeriodVal,
    sortByVal,
    sortDirectionVal,
    currentBlockchain,
    leaderboardChain,
  ])

  if (!leaderboardItems.length) {
    return <EmptyLeaderboard hideFloorChart={hideFloorChart} />
  }

  return (
    <LeaderboardTable
      key={`${statisticPeriodValue}-${leaderboardItems.length}`}
      leaderboardItems={leaderboardItems}
      isHolding={isHolding}
      showBlockchain={showBlockchain}
      hideFloorChart={hideFloorChart}
    />
  )
}, "CollectionLeaderboardContent")

function ErrorFallback({ hideFloorChart }: { hideFloorChart: boolean }) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="w-full overflow-auto">
        <LeaderboardTableLayout hideFloorChart={hideFloorChart}>
          {null}
        </LeaderboardTableLayout>
      </div>
      <ErrorMessage
        retryButton={
          <div>
            {/* TODO: Button with retry logic should be added
          Unfortunatly, there is a known issue with React Query reset and error boundaries
          @see {@link https://github.com/TanStack/query/issues/2712}

          Should be implemented when the issue is resolved or a proper workaround is found */}
          </div>
        }
      />
    </div>
  )
}

function EmptyLeaderboard({ hideFloorChart }: { hideFloorChart: boolean }) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="w-full overflow-auto">
        <LeaderboardTableLayout hideFloorChart={hideFloorChart}>
          {null}
        </LeaderboardTableLayout>
      </div>
      <div className="flex flex-1 items-center justify-center">
        <h2 className="text-center text-2xl">
          <Trans>Nothing here yet...</Trans>
        </h2>
      </div>
    </div>
  )
}

/** Route loading screens render it bare; it then reads the chain itself. */
export const CollectionLeaderboardSkeleton = reatomComponent(
  ({ hideFloorChart }: { hideFloorChart?: boolean }) => {
    const hideOnChain = useHideFloorChart()
    return (
      <LeaderboardTableSkeleton
        hideFloorChart={hideFloorChart ?? hideOnChain}
      />
    )
  },
  "CollectionLeaderboardSkeleton",
)
